import React, { useEffect, useRef, useState } from "react";
import AvatarImg from "./AvatarImg";
import { useLanguage } from "../i18n/LanguageContext";
import { localizeNotificationContent } from "../utils/notificationI18n";

const TYPE_CFG = {
  chat:             { ikon: "💬", renk: "#6366f1", labelKey: "notif.type.chat"      },
  task:             { ikon: "✅", renk: "#8b5cf6", labelKey: "notif.type.task"       },
  taskReminder:     { ikon: "⏰", renk: "#8b5cf6", labelKey: "notif.type.reminder"  },
  shopping:         { ikon: "🛒", renk: "#f59e0b", labelKey: "notif.type.shopping"   },
  calendar:         { ikon: "📅", renk: "#3b82f6", labelKey: "notif.type.calendar"      },
  calendarReminder: { ikon: "📅", renk: "#3b82f6", labelKey: "notif.type.calendar"      },
  finance:          { ikon: "💰", renk: "#10b981", labelKey: "notif.type.finance"      },
  sharedExpense:    { ikon: "💸", renk: "#ec4899", labelKey: "notif.type.expense"     },
  document:         { ikon: "📁", renk: "#14b8a6", labelKey: "notif.type.document"       },
  health:           { ikon: "❤️", renk: "#ef4444", labelKey: "notif.type.health"      },
  medicine:         { ikon: "💊", renk: "#22c55e", labelKey: "notif.type.medicine"        },
  appointment:      { ikon: "🏥", renk: "#8b5cf6", labelKey: "notif.type.appointment"     },
  birthday:         { ikon: "🎂", renk: "#f472b6", labelKey: "notif.type.birthday"  },
  location:         { ikon: "📍", renk: "#06b6d4", labelKey: "notif.type.location"       },
  geofence:         { ikon: "📍", renk: "#06b6d4", labelKey: "notif.type.location"       },
  yakinlik:         { ikon: "📍", renk: "#06b6d4", labelKey: "notif.type.location"       },
  bulusma:          { ikon: "🤝", renk: "#06b6d4", labelKey: "notif.type.location"       },
  familyAnnouncement:{ ikon: "👨‍👩‍👧‍👦", renk: "#6366f1", labelKey: "notif.type.family"        },
  familyStatusUpdated:{ ikon: "📍", renk: "#14b8a6", labelKey: "notif.type.familyStatus"       },
  familyStatusUpdateRequest:{ ikon: "📍", renk: "#14b8a6", labelKey: "notif.type.familyStatus" },
  system:           { ikon: "📢", renk: "#0ea5e9", labelKey: "notif.type.system"      },
  systemAnnouncement:{ ikon: "📢", renk: "#0ea5e9", labelKey: "notif.type.system"    },
  scheduled:        { ikon: "⏰", renk: "#6366f1", labelKey: "notif.type.scheduled"     },
  default:          { ikon: "🔔", renk: "#6366f1", labelKey: "notif.type.default"    },
};

const MAX_GORUNUR = 1;
const MAX_KUYRUK  = 1;
const SURE        = 4500;

function NotificationCard({ notif, onDismiss, onNotificationClick, uyeler }) {
  const { t, lang } = useLanguage();
  const [visible,  setVisible]  = useState(false);
  const [leaving,  setLeaving]  = useState(false);
  const timerRef    = useRef(null);
  const progressRef = useRef(null);
  const startYRef   = useRef(0);
  const lastYRef    = useRef(0);
  const startTimeRef = useRef(0);
  const movedRef    = useRef(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 10);
    timerRef.current = setTimeout(() => dismiss(), SURE);
    return () => { clearTimeout(t1); clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    clearTimeout(timerRef.current);
    setLeaving(true);
    setTimeout(() => onDismiss(notif.id), 380);
  };

  const pauseTimer = () => {
    clearTimeout(timerRef.current);
    if (progressRef.current) progressRef.current.style.animationPlayState = "paused";
  };
  const resumeTimer = () => {
    timerRef.current = setTimeout(() => dismiss(), 1500);
    if (progressRef.current) progressRef.current.style.animationPlayState = "running";
  };

  const handlePointerDown = (e) => {
    if (leaving) return;
    pauseTimer();
    startYRef.current = e.clientY;
    lastYRef.current = e.clientY;
    startTimeRef.current = Date.now();
    movedRef.current = false;
    setDragging(true);

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_) {
      // Bazı tarayıcılarda pointer capture desteklenmeyebilir.
    }
  };

  const handlePointerMove = (e) => {
    if (!dragging || leaving) return;

    const diffY = e.clientY - startYRef.current;
    lastYRef.current = e.clientY;

    // Sadece yukarı sürüklemeyi aktif kullanıyoruz. Aşağı hareketi sayfaya kaptırmamak için sınırlı tutuyoruz.
    const nextY = Math.min(8, Math.max(diffY, -130));

    if (Math.abs(diffY) > 3) {
      movedRef.current = true;
      e.preventDefault();
      e.stopPropagation();
    }

    setDragY(nextY);
  };

  const handlePointerUp = (e) => {
    if (!dragging) return;

    const totalY = lastYRef.current - startYRef.current;
    const elapsed = Math.max(Date.now() - startTimeRef.current, 1);
    const velocityY = totalY / elapsed;
    const shouldDismiss = dragY < -42 || velocityY < -0.45;

    setDragging(false);

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {
      // Sessiz geç.
    }

    if (shouldDismiss) {
      dismiss();
    } else {
      setDragY(0);
      resumeTimer();
    }
  };

  const handlePointerCancel = () => {
    setDragging(false);
    setDragY(0);
    resumeTimer();
  };

  let gonderenUye = null;
  if (notif.gonderenUid && uyeler?.length > 0) {
    gonderenUye = uyeler.find((u) => (u.uid || u.id) === notif.gonderenUid) || null;
  }

  const cfg  = TYPE_CFG[notif.type] || TYPE_CFG.default;
  const localized = localizeNotificationContent(notif, lang);
  const renk = gonderenUye?.calendarColor || cfg.renk;
  const avatarInitials = gonderenUye
    ? `${gonderenUye.ad?.[0] || ""}${gonderenUye.soyad?.[0] || ""}`.toUpperCase()
    : "";

  return (
    <div
      className={`inAppNotif ${visible ? "inAppNotifIn" : ""} ${leaving ? "inAppNotifOut" : ""} ${dragging ? "inAppNotifDragging" : ""}`}
      style={{
        "--notif-renk": renk,
        "--drag-y": `${dragY}px`,
        "--drag-opacity": Math.max(0.35, 1 - Math.abs(dragY) / 130),
      }}
      onClick={(e) => {
        if (movedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          movedRef.current = false;
          return;
        }
        onNotificationClick?.(notif);
        dismiss();
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onMouseEnter={pauseTimer}
      onMouseLeave={() => { if (!dragging) resumeTimer(); }}
    >
      <div className="inAppNotifAccent" style={{ background: renk }} />

      {gonderenUye ? (
        <AvatarImg
          avatar={gonderenUye.avatar || null}
          renk={renk}
          initials={avatarInitials || "?"}
          size={38}
          glow={false}
          radius="12px"
          className="inAppNotifAvatar"
        />
      ) : (
        <div className="inAppNotifAvatar" style={{
          background: `${renk}22`,
          border:     `1.5px solid ${renk}44`,
          fontSize:   20,
        }}>
          {cfg.ikon}
        </div>
      )}

      <div className="inAppNotifMetin">
        <div className="inAppNotifMeta">
          <span className="inAppNotifTip" style={{ color: renk, borderColor: `${renk}33`, background: `${renk}18` }}>
            {t(cfg.labelKey || "notif.type.default")}
          </span>
          <span className="inAppNotifZaman">{t("common.now")}</span>
        </div>
        <div className="inAppNotifBaslik">{localized.baslik}</div>
        <div className="inAppNotifAciklama">{localized.aciklama}</div>
      </div>

      <button className="inAppNotifKapat" onClick={(e) => { e.stopPropagation(); dismiss(); }}>×</button>

      <div
        ref={progressRef}
        className="inAppNotifProgress"
        style={{ background: `linear-gradient(90deg, ${renk}, ${renk}66)`, animationDuration: `${SURE}ms` }}
      />
    </div>
  );
}

// Modül seviyesinde: component unmount/remount'ta sıfırlanmasın.
// Kill+restart sonrası useNotifications zaten gorulenBildirimIds ile toast kontrolü yapıyor;
// bu Set ise aynı oturumda component yeniden mount edilse bile aynı toast'ı tekrar göstermez.
const _islenmisBildirimlerGlobal = new Set();

export default function InAppNotification({ notifications, onDismiss, onNotificationClick, uyeler = [] }) {
  const [gosterilen, setGosterilen]           = useState([]);
  const kuyrukRef                             = useRef([]);
  const gosterilenIdsRef                      = useRef(new Set());
  // Modül-seviyesi Set referansı — her render'da aynı nesne
  const islenmisBildirimlerRef                = useRef(_islenmisBildirimlerGlobal);

  useEffect(() => {
    const yeniGeleler = notifications.filter((n) => !islenmisBildirimlerRef.current.has(n.id));
    if (yeniGeleler.length > 0) {
      yeniGeleler.forEach((n) => islenmisBildirimlerRef.current.add(n.id));
      // Mevcut gösterilen + kuyruktaki sayı MAX sınırının altındaysa kuyruğa ekle
      const mevcutToplam = gosterilenIdsRef.current.size + kuyrukRef.current.length;
      const alinabilecek = Math.max(0, MAX_GORUNUR + MAX_KUYRUK - mevcutToplam);
      if (alinabilecek > 0) {
        kuyrukRef.current = [...kuyrukRef.current, ...yeniGeleler.slice(0, alinabilecek)];
      }
      // Limit aşıldıysa geri kalanları sessizce yok say (artık toast çıkmasın)
    }
    if (gosterilenIdsRef.current.size < MAX_GORUNUR && kuyrukRef.current.length > 0) {
      const eklenecek = kuyrukRef.current.splice(0, MAX_GORUNUR - gosterilenIdsRef.current.size);
      eklenecek.forEach((n) => gosterilenIdsRef.current.add(n.id));
      setGosterilen((prev) => [...prev, ...eklenecek]);
    }
  }, [notifications]);

  const handleDismiss = (id) => {
    onDismiss(id);
    gosterilenIdsRef.current.delete(id);
    setGosterilen((prev) => prev.filter((n) => n.id !== id));
    if (kuyrukRef.current.length > 0) {
      setTimeout(() => {
        if (kuyrukRef.current.length > 0) {
          const sonraki = kuyrukRef.current.splice(0, 1)[0];
          gosterilenIdsRef.current.add(sonraki.id);
          setGosterilen((prev) => [...prev, sonraki]);
        }
      }, 400);
    }
  };

  if (gosterilen.length === 0) return null;

  return (
    <div className="inAppNotifContainer">
      {gosterilen.map((notif, index) => (
        <NotificationCard
          key={notif.id}
          notif={notif}
          onDismiss={handleDismiss}
          onNotificationClick={onNotificationClick}
          uyeler={uyeler}
          index={index}
        />
      ))}
    </div>
  );
}
